// minify 後も Sentry で型名が残るよう、各クラスの name をクラスフィールドで文字列として明示する

/**
 * HTTP クライアントが投げるエラーの基底クラス。
 * `instanceof HttpError` で HTTP 通信の失敗とプログラムの不具合を見分けられる。
 * 2xx で body が JSON として読めず、HTML でもない場合は、このクラスを直接投げる。
 */
export class HttpError extends Error {
  name = 'HttpError';
}

/**
 * サーバーが 2xx 以外のステータスを返したことを表す。
 * `body` はサーバーが返した形のまま（JSON、text、空なら null）で、camelCase 化しない。
 * 4xx と 5xx は `ClientError` / `ServerError` で投げられるため、このクラスを直接受け取るのはそれ以外のステータスだけ。
 */
export class ResponseError extends HttpError {
  name = 'ResponseError';
  readonly status: number;
  readonly body: unknown;

  constructor(method: string, path: string, status: number, body: unknown) {
    super(`${method} ${path} responded ${status}`);
    this.status = status;
    this.body = body;
  }
}

/**
 * サーバーが 4xx を返したことを表す。
 * 422 のバリデーションエラーは `error instanceof ClientError && error.status === 422` で判定し、`body` から項目ごとのエラーを読む。
 */
export class ClientError extends ResponseError {
  name = 'ClientError';
}

/**
 * サーバーが 5xx を返したことを表す。
 */
export class ServerError extends ResponseError {
  name = 'ServerError';
}

/**
 * 通信そのものが失敗したことを表す（オフライン、DNS、CORS、body の読み取り中の切断など）。
 * `cause` に元の TypeError を持つ。利用者の環境が原因のため Sentry には送らない。
 */
export class NetworkError extends HttpError {
  name = 'NetworkError';
}

/**
 * fetch（ヘッダー受信まで）か body の読み取りが `timeoutMs` を超えたことを表す。
 * サーバー側（ALB や Nginx）のタイムアウトは 408 の `ClientError` になり、こちらではない。
 */
export class TimeoutError extends HttpError {
  name = 'TimeoutError';
}

/**
 * 2xx なのに HTML が返ったことを表す（セッション切れでログイン画面へリダイレクトされた、CSRF 失敗の画面など）。
 * 利用者の状態が原因のため Sentry には送らない。
 */
export class UnexpectedHtmlError extends HttpError {
  name = 'UnexpectedHtmlError';
}

/**
 * ステータスに応じて `ClientError`（4xx）、`ServerError`（5xx）、それ以外は `ResponseError` を返す。
 */
export function responseErrorFor(
  method: string,
  path: string,
  status: number,
  body: unknown,
): ResponseError {
  if (status >= 500 && status <= 599) {
    return new ServerError(method, path, status, body);
  }
  if (status >= 400 && status <= 499) {
    return new ClientError(method, path, status, body);
  }
  return new ResponseError(method, path, status, body);
}
